import {useTranslation} from 'react-i18next';
import {Divider, Stack} from '@mui/material';
import SelectComponent from '@components/inputs/select/Select.component';
import {Sizes} from '@constants/sizes.constants';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import React, {useEffect, useState} from 'react';
import {IssueKind, IssueKindType, issuesService, IssueStatus, ManageIssue} from '@store/operations/issues';
import TimeTextFieldComponent from '@components/inputs/timePicker/TimeTextFieldComponent';
import {useSnackbar} from 'notistack';

interface IssueDetailsFormProps {
  errors: any;
  issue: ManageIssue;
  isCreation?: boolean;
  updateIssue: (data: ManageIssue) => void;
}

const IssueDetailsForm = (props: IssueDetailsFormProps) => {
  const {
    issue,
    errors,
    isCreation,
    updateIssue,
  } = props;

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const [issueKinds, setIssueKinds] = useState<IssueKind[]>([]);

  useEffect(() => {
    issuesService.getIssueKinds().subscribe({
      next: setIssueKinds,
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  }, [enqueueSnackbar]);

  return (
    <>
      <Stack spacing={2}>
        {!isCreation && (
          <SelectComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('operations.nonCompliance.manage.category')}
            placeholder={t('global.selectOption')}
            value={issue.issueKind?.type}
            readOnly
            items={IssueKindType.selectItems()}
            required
          />
        )}

        <SelectComponent
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('operations.nonCompliance.manage.type')}
          placeholder={t('global.selectOption')}
          handleChange={(issueKind) => updateIssue({
            issueKind: {
              name: issue.issueKind?.name ?? '',
              type: issue.issueKind?.type ?? IssueKindType.OTHER,
              id: issueKind,
              translationKey: issue.issueKind?.translationKey ?? '',
            }
          })}
          error={errors.issueKind}
          value={issue.issueKind?.id}
          items={issueKinds.map((ik) => ({label: t(ik.translationKey), value: ik.id}))}
          readOnly={!isCreation}
          required/>
        <Stack direction={'row'}>
          <DatePickerComponent
            labelWidth={Sizes.datePickerWidth}
            disablePast
            label={t('operations.nonCompliance.manage.dateIssue')}
            placeholder={t('global.selectDate')}
            handleChange={(reportedAt) => updateIssue({reportedAt})}
            error={errors.reportedAt}
            value={issue.reportedAt}
            readOnly={!isCreation}
            required/>
          <TimeTextFieldComponent
            value={issue.dateHour}
            handleChange={(dateHour) => updateIssue({dateHour: dateHour ?? undefined})}
            label={t('operations.nonCompliance.manage.hourIssue')}
            labelWidth={Sizes.drawerInputLabelWidth}
            error={errors.dateHour}
            readOnly={!isCreation}
            required/>
        </Stack>
        <TextFieldComponent
          readOnly
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('operations.nonCompliance.manage.phoneNumber')}
          placeholder={t('operations.nonCompliance.manage.phoneNumber')}
          value={issue.phoneNumber}
          handleChange={(description) => updateIssue({description})}/>
        <TextFieldComponent
          error={errors.comment}
          multiline
          minRows={5}
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('operations.nonCompliance.manage.description')}
          placeholder={t('operations.nonCompliance.manage.description')}
          value={issue.description}
          handleChange={(description) => updateIssue({description})}/>
      </Stack>
      <Divider variant="fullWidth" orientation="horizontal"/>
      {!isCreation && (
        <>
          <SelectComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('global.status')}
            placeholder={t('global.selectOption')}
            handleChange={(status) => updateIssue({status})}
            error={errors.status}
            value={issue.status}
            items={IssueStatus.selectItems()}
            required/>
          {issue.status === IssueStatus.CLOSED && (
            <Stack spacing={2}>
              <TextFieldComponent
                multiline
                minRows={5}
                labelWidth={Sizes.drawerInputLabelWidth}
                label={t('operations.nonCompliance.manage.resolution')}
                placeholder={t('operations.nonCompliance.manage.resolution')}
                value={issue.resolution}
                error={errors.resolution}
                readOnly={issue.status === IssueStatus.CLOSED && issue.resolvedAt != null}
                handleChange={(resolution) => updateIssue({resolution})}/>
              {issue.resolver && (
                <TextFieldComponent
                  labelWidth={Sizes.drawerInputLabelWidth}
                  label={t('global.resolvedBy')}
                  value={`${issue.resolver?.firstName} ${issue.resolver?.lastName}`}
                  readOnly/>
              )}
              {issue.resolvedAt && (
                <Stack direction={'row'}>
                  <DatePickerComponent
                    labelWidth={Sizes.datePickerWidth}
                    disablePast
                    label={t('operations.nonCompliance.manage.closedAtIssue')}
                    placeholder={t('global.selectDate')}
                    value={issue.resolvedAt}
                    readOnly
                    required/>
                  <TimeTextFieldComponent
                    value={issue.resolvedAt}
                    label={t('operations.nonCompliance.manage.hourIssue')}
                    labelWidth={Sizes.drawerInputLabelWidth}
                    readOnly
                    required/>
                </Stack>
              )}

            </Stack>

          )}

          <Divider variant="fullWidth" orientation="horizontal"/>
          {/*<StatusHistoryComponent issue={issue}/>*/}
        </>
      )}
    </>
  );
};

export default IssueDetailsForm;
